import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';

// WCH Strike & Vendor Scorecard (R18 #14). Two tracks off one infraction
// schedule: W2 cleaners accrue STRIKES (rolling 90d), vendors a DEDUCTION RATE.
// Admin/director only — performance data is sensitive. Backed by the ao_strikes
// store via /api/assetops/strikes*.
const API = '/api/assetops';

export class StrikesPage {
  private container: HTMLElement;
  private tab: 'summary' | 'candidates' | 'log' | 'roster' = 'summary';
  private summary: any = null;
  private schedule: any[] = [];
  private inspectorSchedule: any[] = [];
  private staff: { name: string; kind: string; company?: string }[] = [];   // roster for the person picker
  private candidates: any[] = [];
  private log: any[] = [];
  private roster: { people: any[]; vendors: any[] } = { people: [], vendors: [] };
  // R114 fix (data-safety #0): the roster is a full-replace editor — a failed
  // load shows no editor, and the save carries the version it was drawn from.
  private rosterLoaded = false;
  private rosterVersion = '';
  private loading = false;
  private note = '';
  // add-strike form state
  private fPerson = '';
  private fType: 'W2' | 'Vendor' | 'Inspector' = 'W2';
  private fCompany = '';
  private fInfraction = '';
  private fSource = '';
  private fEvidence = '';
  private saving = false;

  constructor(container: HTMLElement) { this.container = container; }
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="strk-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    await this.load();
    this.draw();
  }

  private async load(): Promise<void> {
    const [sum, sch, log, ros, cand, cl, mg, team] = await Promise.all([
      fetch(`${API}/strikes/summary`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/strikes/schedule`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/strikes?days=180`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/strikes/roster`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/strikes/candidates?days=60`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/cleaners`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/inspections/managers`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`/api/tasks/assignees`).then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    this.summary = sum;
    this.schedule = (sch && sch.schedule) || [];
    this.inspectorSchedule = (sch && sch.inspector_schedule) || [];
    this.log = (log && log.rows) || [];
    this.roster = { people: (ros && ros.people) || [], vendors: (ros && ros.vendors) || [] };
    this.rosterLoaded = !!(ros && ros.version);
    this.rosterVersion = (ros && ros.version) || '';
    this.candidates = (cand && cand.candidates) || [];
    // Build the person picker: cleaners (W2/Vendor + company), inspectors (managers),
    // and the wider team. Deduped by name.
    const seen = new Set<string>(); const staff: { name: string; kind: string; company?: string }[] = [];
    const add = (name: string, kind: string, company?: string) => { const k = name.trim().toLowerCase(); if (name.trim() && !seen.has(k)) { seen.add(k); staff.push({ name: name.trim(), kind, company }); } };
    (((cl && cl.cleaners) || []).filter((x: any) => x.active !== false)).forEach((x: any) => add(String(x.name || ''), x.kind === 'vendor' ? 'Vendor' : 'W2', x.company || ''));
    ((((mg && (mg.managers || mg.inspectors)) || []))).forEach((x: any) => add(String(x.name || ''), 'Inspector'));
    (((team && team.people) || [])).forEach((x: any) => add(String(x.full_name || x.email || ''), x.role === 'inspector' ? 'Inspector' : 'Team'));
    this.staff = staff;
  }

  private draw(): void {
    const body = document.getElementById('strk-body');
    if (!body) return;
    if (!this.summary) { body.innerHTML = emptyState('Performance data unavailable', 'This view is limited to Admin and Director roles.', '🔒'); return; }
    body.innerHTML = `
      <style>
        .strk-head { display:flex; align-items:baseline; gap:12px; margin-bottom:6px; flex-wrap:wrap; }
        .strk-head h2 { margin:0; font-size:19px; font-weight:600; }
        .strk-head .sub { font-size:12px; color:var(--text-muted); }
        .strk-tabs { display:flex; gap:6px; margin:14px 0 16px; flex-wrap:wrap; }
        .strk-tab { font-size:12.5px; font-weight:600; padding:7px 14px; border:1px solid var(--border); border-radius:999px; background:#fff; color:var(--text-muted); cursor:pointer; }
        .strk-tab.on { background:var(--text); color:#fff; border-color:var(--text); }
        .strk-sec-h { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin:20px 0 8px; }
        .strk-table { width:100%; border-collapse:collapse; font-size:13px; background:var(--card); border:1px solid var(--border); border-radius:var(--radius); overflow:hidden; }
        .strk-table th { text-align:left; padding:9px 12px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); border-bottom:1px solid var(--border); background:var(--bg-subtle); }
        .strk-table td { padding:9px 12px; border-bottom:1px solid var(--border-soft, #f1f5f9); vertical-align:top; }
        .strk-pill { display:inline-block; font-size:11px; font-weight:700; padding:2px 9px; border-radius:999px; }
        .strk-band-clear { background:#f0fdf4; color:#15803d; } .strk-band-coach { background:#fefce8; color:#a16207; }
        .strk-band-warn { background:#fff7ed; color:#c2410c; } .strk-band-bad { background:#fef2f2; color:#b91c1c; }
        .strk-imm { color:#b91c1c; font-weight:700; font-size:11.5px; }
        .strk-num { font-variant-numeric:tabular-nums; font-weight:700; }
        .strk-form { background:var(--card); border:1px solid var(--border); border-radius:12px; padding:16px; margin-bottom:18px; display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        .strk-form .full { grid-column:1/-1; }
        .strk-form label { display:flex; flex-direction:column; gap:4px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); }
        .strk-form input, .strk-form select { padding:8px 10px; border:1px solid var(--border); border-radius:8px; font-size:13px; }
        .strk-btn { font-size:13px; font-weight:600; padding:9px 16px; border:none; border-radius:9px; background:var(--accent); color:#fff; cursor:pointer; }
        .strk-btn:disabled { opacity:.6; }
        .strk-mini { font-size:11px; padding:3px 8px; border:1px solid var(--border); border-radius:6px; background:#fff; color:var(--accent); cursor:pointer; }
        .strk-rin { padding:6px 9px; border:1px solid var(--border); border-radius:7px; font-size:13px; width:100%; }
        @media (max-width:720px) { .strk-form { grid-template-columns:1fr; } .strk-table { font-size:12px; } .strk-table th, .strk-table td { padding:7px 8px; } }
      </style>
      <div class="strk-head">
        <h2>Team Performance</h2>
        <span class="sub">strikes (W2 cleaners + inspectors) + vendor scorecard · rolling 90 days · admin/director only</span>
      </div>
      <div class="strk-tabs">
        <button class="strk-tab ${this.tab === 'summary' ? 'on' : ''}" data-strk-tab="summary">Summary</button>
        <button class="strk-tab ${this.tab === 'candidates' ? 'on' : ''}" data-strk-tab="candidates">Review queue${this.candidates.length ? ` <span style="background:#b91c1c; color:#fff; border-radius:999px; padding:0 6px; font-size:10px; margin-left:2px;">${this.candidates.length}</span>` : ''}</button>
        <button class="strk-tab ${this.tab === 'log' ? 'on' : ''}" data-strk-tab="log">Strike log</button>
        <button class="strk-tab ${this.tab === 'roster' ? 'on' : ''}" data-strk-tab="roster">Vendor roster</button>
      </div>
      ${this.tab === 'summary' ? this.summaryHtml() : this.tab === 'candidates' ? this.candidatesHtml() : this.tab === 'roster' ? this.rosterHtml() : this.logHtml()}
      ${this.note ? `<div style="margin-top:10px; font-size:12px; color:var(--text-muted);">${this.esc(this.note)}</div>` : ''}`;
    this.bind();
  }

  private bandClass(band: string): string {
    if (/Termination|Final|Probation|Watch|Removal/.test(band)) return 'strk-band-bad';
    if (/Written warning/.test(band)) return 'strk-band-warn';
    if (/Coaching|Standard/.test(band)) return 'strk-band-coach';
    return 'strk-band-clear';
  }

  private personRow(p: any): string {
    return `
      <tr>
        <td><strong>${this.esc(p.person)}</strong>${p.open_immediate.length ? `<div class="strk-imm">⚠ IMMEDIATE: ${this.esc(p.open_immediate.join(', '))}</div>` : ''}</td>
        <td class="strk-num">${p.active_strikes}</td>
        <td class="strk-num">${p.last_30d}</td>
        <td><span class="strk-pill ${this.bandClass(p.status)}">${this.esc(p.status)}</span></td>
        <td style="font-size:12px; color:var(--text-muted);">${this.esc(p.action)}</td>
        <td style="font-size:12px;">${this.esc(p.last_strike || '—')}</td>
      </tr>`;
  }

  private summaryHtml(): string {
    const people: any[] = this.summary.people || [];
    const inspectors: any[] = this.summary.inspectors || [];
    const vendors: any[] = this.summary.vendors || [];
    const peopleRows = people.map(p => this.personRow(p)).join('') || `<tr><td colspan="6" style="text-align:center; color:var(--text-muted); padding:20px;">No W2 strikes logged.</td></tr>`;
    const inspectorRows = inspectors.map(p => this.personRow(p)).join('') || `<tr><td colspan="6" style="text-align:center; color:var(--text-muted); padding:20px;">No inspector strikes logged.</td></tr>`;
    const vendorRows = vendors.map(v => `
      <tr>
        <td><strong>${this.esc(v.company)}</strong>${v.open_immediate.length ? `<div class="strk-imm">⚠ IMMEDIATE: ${this.esc(v.open_immediate.join(', '))}</div>` : ''}</td>
        <td class="strk-num">${v.points}</td>
        <td class="strk-num">${v.cleans}</td>
        <td class="strk-num">${v.deduction_rate == null ? '—' : v.deduction_rate}</td>
        <td><span class="strk-pill ${this.bandClass(v.tier)}">${this.esc(v.tier)}</span></td>
        <td style="font-size:12px; color:var(--text-muted);">${this.esc(v.standing)}</td>
      </tr>`).join('') || `<tr><td colspan="6" style="text-align:center; color:var(--text-muted); padding:20px;">No vendor deductions logged. Set vendor clean volumes so rates compute.</td></tr>`;
    return `
      <div class="strk-sec-h">W2 cleaners — active strikes (rolling 90 days)</div>
      <table class="strk-table">
        <thead><tr><th>Person</th><th>Active</th><th>Last 30d</th><th>Status</th><th>Required action</th><th>Last strike</th></tr></thead>
        <tbody>${peopleRows}</tbody>
      </table>
      <div class="strk-sec-h">Inspectors — active strikes (rolling 90 days)</div>
      <table class="strk-table">
        <thead><tr><th>Inspector</th><th>Active</th><th>Last 30d</th><th>Status</th><th>Required action</th><th>Last strike</th></tr></thead>
        <tbody>${inspectorRows}</tbody>
      </table>
      <div class="strk-sec-h">Vendors — deduction rate = points ÷ cleans × 100</div>
      <table class="strk-table">
        <thead><tr><th>Vendor</th><th>Points</th><th>Cleans</th><th>Rate</th><th>Tier</th><th>Standing</th></tr></thead>
        <tbody>${vendorRows}</tbody>
      </table>`;
  }

  private candidatesHtml(): string {
    if (!this.candidates.length) {
      return `<div class="strk-sec-h">Potential strikes the system caught</div>
        <div style="font-size:13px; color:var(--text-muted); background:var(--card); border:1px solid var(--border); border-radius:12px; padding:18px;">Nothing to review. The system scans the last 60 days of guest reviews (≤3★ mentioning cleanliness) and open guest-message cleanliness tickets, matches each to the cleaner of the most recent completed turn, and surfaces them here to log or dismiss.</div>`;
    }
    const cards = this.candidates.map((c, i) => {
      const primary = (c.cleaners && c.cleaners[0]) || null;
      const who = c.cleaners && c.cleaners.length
        ? c.cleaners.map((x: any) => `${this.esc(x.name)}${x.type === 'Vendor' ? ` (${this.esc(x.company || 'vendor')})` : ''}`).join(' + ')
        : '<span style="color:#b91c1c;">cleaner not matched — no completed turn on file before this date</span>';
      const src = c.kind === 'review' ? `Guest review · ${c.stars}★` : `Guest message · ${this.esc(c.severity)}`;
      return `
        <div style="background:var(--card); border:1px solid var(--border); border-left:3px solid #f59e0b; border-radius:12px; padding:14px; margin-bottom:10px;">
          <div style="display:flex; justify-content:space-between; gap:12px; flex-wrap:wrap; align-items:baseline;">
            <strong style="font-size:14px;">${this.esc(c.listing_name)}</strong>
            <span style="font-size:11.5px; color:var(--text-muted);">${src} · ${this.esc(c.date)}</span>
          </div>
          <div style="font-size:13px; color:var(--text); margin:7px 0; font-style:italic;">“${this.esc(c.snippet)}”</div>
          <div style="font-size:12px; color:var(--text-muted);">Assigned cleaner (turn ${this.esc(c.turn_date || '—')}): <strong style="color:var(--text);">${who}</strong></div>
          <div style="font-size:12px; color:var(--text-muted); margin-top:3px;">Suggested: <strong>${this.esc(c.suggested_infraction)}</strong> · ${this.esc(c.suggested_type)}</div>
          <div style="margin-top:10px; display:flex; gap:8px;">
            <button class="strk-btn" data-cand-log="${i}" ${primary ? '' : 'disabled title="Match a cleaner first — no turn on file"'} style="padding:7px 13px; font-size:12.5px;">Log strike →</button>
            <button class="strk-mini" data-cand-dismiss="${this.esc(c.key)}">Dismiss</button>
          </div>
        </div>`;
    }).join('');
    return `<div class="strk-sec-h">Potential strikes the system caught · ${this.candidates.length}</div>
      <div style="font-size:12px; color:var(--text-muted); margin-bottom:10px;">Auto-detected from guest reviews + messages. Review each, then <strong>Log strike</strong> (pre-fills the log with the matched cleaner + evidence) or <strong>Dismiss</strong>.</div>
      ${cards}`;
  }

  private logHtml(): string {
    const activeSchedule = this.fType === 'Inspector' ? this.inspectorSchedule : this.schedule;
    const opts = activeSchedule.filter(s => s.value !== -1).map(s =>
      `<option value="${this.esc(s.infraction)}" ${this.fInfraction === s.infraction ? 'selected' : ''}>${this.esc(s.category)} — ${this.esc(s.infraction)} (${s.value === 99 ? 'TERM' : s.value})</option>`).join('');
    const logRows = this.log.map(r => `
      <tr>
        <td style="font-size:12px;">${this.esc(r.date)}</td>
        <td><strong>${this.esc(r.person)}</strong> <span style="font-size:11px; color:var(--text-muted);">${this.esc(r.type)}${r.company ? ` · ${this.esc(r.company)}` : ''}</span></td>
        <td style="font-size:12px;">${this.esc(r.infraction)}</td>
        <td class="strk-num">${r.value === 99 ? 'TERM' : r.value}</td>
        <td style="font-size:12px; color:var(--text-muted);">${this.esc(r.evidence)}</td>
        <td style="font-size:11px;">${r.notified ? '✓ notified' : '<button class="strk-mini" data-strk-notify="' + this.esc(r.id) + '">mark notified</button>'} ${r.disputed ? '· disputed' : ''}</td>
      </tr>`).join('') || `<tr><td colspan="6" style="text-align:center; color:var(--text-muted); padding:20px;">No strikes logged yet.</td></tr>`;
    // People picker filtered by the selected type (free-text still allowed).
    const peopleFor = this.fType === 'Inspector'
      ? this.staff.filter(p => p.kind === 'Inspector' || p.kind === 'Team')
      : this.fType === 'Vendor'
        ? this.staff.filter(p => p.kind === 'Vendor')
        : this.staff.filter(p => p.kind === 'W2' || p.kind === 'Team');
    const personOpts = peopleFor.map(p => `<option value="${this.esc(p.name)}">${p.company ? this.esc(p.company) : this.esc(p.kind)}</option>`).join('');
    return `
      <div class="strk-form">
        <label class="full">Person
          <input id="strk-person" list="strk-people" autocomplete="off" value="${this.esc(this.fPerson)}" placeholder="${this.fType === 'Inspector' ? 'Pick or type an inspector' : 'Pick or type a name'}">
          <datalist id="strk-people">${personOpts}</datalist>
        </label>
        <label>Type
          <select id="strk-type"><option value="W2" ${this.fType === 'W2' ? 'selected' : ''}>W2 cleaner (in-house)</option><option value="Vendor" ${this.fType === 'Vendor' ? 'selected' : ''}>Vendor cleaner</option><option value="Inspector" ${this.fType === 'Inspector' ? 'selected' : ''}>Inspector</option></select>
        </label>
        <label>Company (vendor only)
          <input id="strk-company" value="${this.esc(this.fCompany)}" placeholder="Vendor company">
        </label>
        <label class="full">Infraction
          <select id="strk-infraction"><option value="">— pick from the schedule —</option>${opts}</select>
        </label>
        <label>Source
          <input id="strk-source" value="${this.esc(this.fSource)}" placeholder="Inspection / Review / Report">
        </label>
        <label>Evidence (required)
          <input id="strk-evidence" value="${this.esc(this.fEvidence)}" placeholder="INSP-1042 / photo 3 / review link">
        </label>
        <div class="full"><button class="strk-btn" id="strk-add" ${this.saving ? 'disabled' : ''}>${this.saving ? 'Logging…' : 'Log strike'}</button> <span style="font-size:11.5px; color:var(--text-muted); margin-left:8px;">Value auto-fills from the schedule. Nothing counts without evidence.</span></div>
      </div>
      <table class="strk-table">
        <thead><tr><th>Date</th><th>Person</th><th>Infraction</th><th>Val</th><th>Evidence</th><th></th></tr></thead>
        <tbody>${logRows}</tbody>
      </table>`;
  }

  private rosterHtml(): string {
    if (!this.rosterLoaded) {
      return `<div class="strk-sec-h">Vendor roster</div>
        <div role="alert" style="display:flex; gap:12px; align-items:center; flex-wrap:wrap; padding:14px; border:1px solid var(--border); border-radius:var(--radius); background:var(--card);">
          <div style="flex:1 1 240px; font-size:13px; color:var(--danger-text);">Couldn't load the vendor roster just now — nothing was changed. Reload the page in a moment.</div>
          <button class="strk-btn" id="strk-roster-reload" style="min-height:36px;">Reload</button>
        </div>`;
    }
    const people: any[] = this.roster.people || [];
    const vendors: any[] = this.roster.vendors || [];
    const pRows = people.map((p, i) => `
      <tr>
        <td><input class="strk-rin" data-rp="name|${i}" value="${this.esc(p.name)}" placeholder="Cleaner name"></td>
        <td><input class="strk-rin" data-rp="company|${i}" value="${this.esc(p.company)}" placeholder="Vendor company"></td>
        <td><button class="strk-mini" data-rp-del="${i}">✕</button></td>
      </tr>`).join('');
    const vRows = vendors.map((v, i) => `
      <tr>
        <td><input class="strk-rin" data-rv="company|${i}" value="${this.esc(v.company)}" placeholder="Vendor company"></td>
        <td><input class="strk-rin" type="number" min="0" data-rv="cleans_90d|${i}" value="${this.esc(v.cleans_90d)}" placeholder="0" style="width:90px;"></td>
        <td><button class="strk-mini" data-rv-del="${i}">✕</button></td>
      </tr>`).join('');
    return `
      <div class="strk-sec-h">Vendor cleaners — map a cleaner to a vendor company</div>
      <div style="font-size:12px; color:var(--text-muted); margin-bottom:8px;">A cleaner listed here counts their completed cleans toward that vendor's deduction rate automatically.</div>
      <table class="strk-table">
        <thead><tr><th>Cleaner name</th><th>Vendor company</th><th></th></tr></thead>
        <tbody>${pRows || '<tr><td colspan="3" style="color:var(--text-muted); padding:14px;">No vendor cleaners yet.</td></tr>'}</tbody>
      </table>
      <button class="strk-mini" id="strk-rp-add" style="margin-top:8px;">+ Add cleaner</button>
      <div class="strk-sec-h">Vendor clean volume (manual fallback — used when cleaners aren't mapped above)</div>
      <table class="strk-table">
        <thead><tr><th>Vendor company</th><th>Cleans (90d)</th><th></th></tr></thead>
        <tbody>${vRows || '<tr><td colspan="3" style="color:var(--text-muted); padding:14px;">No vendors yet.</td></tr>'}</tbody>
      </table>
      <button class="strk-mini" id="strk-rv-add" style="margin-top:8px;">+ Add vendor</button>
      <div style="margin-top:16px;"><button class="strk-btn" id="strk-roster-save" ${this.saving ? 'disabled' : ''}>${this.saving ? 'Saving…' : 'Save roster'}</button></div>`;
  }

  private bind(): void {
    this.container.querySelectorAll<HTMLElement>('[data-strk-tab]').forEach(el => el.addEventListener('click', () => { this.tab = el.dataset.strkTab as any; this.draw(); }));
    // Review-queue candidates → one-click prefill of the log form, or dismiss.
    this.container.querySelectorAll<HTMLElement>('[data-cand-log]').forEach(el => el.addEventListener('click', () => {
      const c = this.candidates[Number(el.dataset.candLog)];
      if (!c) return;
      const primary = (c.cleaners && c.cleaners[0]) || null;
      if (!primary) { this.note = 'No cleaner matched — assign the turn a cleaner first.'; this.draw(); return; }
      this.fPerson = primary.name;
      this.fType = primary.type === 'Vendor' ? 'Vendor' : 'W2';
      this.fCompany = primary.type === 'Vendor' ? (primary.company || c.suggested_company || '') : '';
      this.fInfraction = c.suggested_infraction || '';
      this.fSource = c.kind === 'review' ? 'Guest review' : 'Guest message';
      this.fEvidence = `${c.listing_name} · ${c.date} · “${String(c.snippet || '').slice(0, 120)}” [${c.key}]`;
      this.tab = 'log';
      this.note = 'Pre-filled from a review-queue candidate. Confirm the cleaner + infraction, then Log strike.';
      this.draw();
    }));
    this.container.querySelectorAll<HTMLElement>('[data-cand-dismiss]').forEach(el => el.addEventListener('click', async () => {
      const key = el.dataset.candDismiss!;
      await fetch(`${API}/strikes/candidates/dismiss`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key }) }).catch(() => null);
      this.candidates = this.candidates.filter(c => c.key !== key);
      this.draw();
    }));
    // roster editor
    const collectRoster = () => {
      this.container.querySelectorAll<HTMLInputElement>('[data-rp]').forEach(inp => { const [f, i] = inp.dataset.rp!.split('|'); if (this.roster.people[+i]) this.roster.people[+i][f] = inp.value; });
      this.container.querySelectorAll<HTMLInputElement>('[data-rv]').forEach(inp => { const [f, i] = inp.dataset.rv!.split('|'); if (this.roster.vendors[+i]) this.roster.vendors[+i][f] = f === 'cleans_90d' ? Number(inp.value) || 0 : inp.value; });
    };
    this.container.querySelector('#strk-rp-add')?.addEventListener('click', () => { collectRoster(); this.roster.people.push({ name: '', type: 'Vendor', company: '' }); this.draw(); });
    this.container.querySelector('#strk-rv-add')?.addEventListener('click', () => { collectRoster(); this.roster.vendors.push({ company: '', cleans_90d: 0 }); this.draw(); });
    this.container.querySelectorAll<HTMLElement>('[data-rp-del]').forEach(el => el.addEventListener('click', () => { collectRoster(); this.roster.people.splice(Number(el.dataset.rpDel), 1); this.draw(); }));
    this.container.querySelectorAll<HTMLElement>('[data-rv-del]').forEach(el => el.addEventListener('click', () => { collectRoster(); this.roster.vendors.splice(Number(el.dataset.rvDel), 1); this.draw(); }));
    this.container.querySelector('#strk-roster-save')?.addEventListener('click', async () => {
      collectRoster();
      this.saving = true; this.draw();
      const people = this.roster.people.filter((p: any) => String(p.name || '').trim()).map((p: any) => ({ name: p.name, type: 'Vendor', company: p.company }));
      const r = await fetch(`${API}/strikes/roster`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ people, vendors: this.roster.vendors, base_version: this.rosterVersion }) }).catch(() => null);
      const j = r ? await r.json().catch(() => null) : null;
      this.saving = false;
      if (!r || !r.ok) {
        // 409/503: nothing was saved; the edits stay on screen with the server's reason
        this.note = `Roster not saved — ${(j && j.error) || 'try again.'}`;
        this.draw();
        return;
      }
      await this.load();
      this.note = 'Roster saved.';
      this.draw();
    });
    this.container.querySelector('#strk-roster-reload')?.addEventListener('click', async () => { await this.load(); this.draw(); });
    const g = (id: string) => (this.container.querySelector<HTMLInputElement | HTMLSelectElement>(id)?.value ?? '');
    const sync = () => { this.fPerson = g('#strk-person'); this.fType = g('#strk-type') as any; this.fCompany = g('#strk-company'); this.fInfraction = g('#strk-infraction'); this.fSource = g('#strk-source'); this.fEvidence = g('#strk-evidence'); };
    this.container.querySelectorAll<HTMLElement>('#strk-person,#strk-company,#strk-infraction,#strk-source,#strk-evidence').forEach(el => el.addEventListener('change', sync));
    // Picking a known vendor cleaner auto-fills the company + forces Vendor type.
    this.container.querySelector('#strk-person')?.addEventListener('change', () => {
      const picked = this.staff.find(p => p.name.toLowerCase() === (g('#strk-person') || '').trim().toLowerCase());
      if (picked && picked.kind === 'Vendor' && picked.company) { sync(); this.fType = 'Vendor'; this.fCompany = picked.company; this.draw(); }
    });
    // Type change swaps the infraction schedule (W2/Vendor vs Inspector) → redraw.
    this.container.querySelector('#strk-type')?.addEventListener('change', () => {
      sync();
      const activeSchedule = this.fType === 'Inspector' ? this.inspectorSchedule : this.schedule;
      if (this.fInfraction && !activeSchedule.some((s: any) => s.infraction === this.fInfraction)) this.fInfraction = '';
      this.draw();
    });
    this.container.querySelector('#strk-add')?.addEventListener('click', async () => {
      sync();
      if (!this.fPerson.trim()) { this.note = 'Person is required.'; this.draw(); return; }
      if (!this.fInfraction) { this.note = 'Pick an infraction.'; this.draw(); return; }
      if (!this.fEvidence.trim()) { this.note = 'Evidence is required — nothing counts without it.'; this.draw(); return; }
      this.saving = true; this.note = ''; this.draw();
      const r = await fetch(`${API}/strikes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ person: this.fPerson, type: this.fType, company: this.fCompany, infraction: this.fInfraction, source: this.fSource, evidence: this.fEvidence }) });
      const j = await r.json().catch(() => ({} as any));
      this.saving = false;
      if (!r.ok) { this.note = j.error || 'Failed to log.'; this.draw(); return; }
      this.fInfraction = ''; this.fEvidence = ''; this.fSource = '';
      await this.load();
      this.note = 'Strike logged.';
      this.draw();
    });
    this.container.querySelectorAll<HTMLElement>('[data-strk-notify]').forEach(el => el.addEventListener('click', async () => {
      await fetch(`${API}/strikes/${el.dataset.strkNotify}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notified: true }) }).catch(() => null);
      await this.load(); this.draw();
    }));
  }
}
